import API_BASE from './config';
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Helmet } from "react-helmet";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import RegistrationForm from './RegistrationForm';
import SuccessModal from './SuccessModal';
import Scanner from './Scanner';
import Dashboard from './Dashboard';
import Portal from './Portal';
import Login from './Login';
import EventCard from './EventCard';
import TheaterMode from './TheaterMode';
import ParticipantLogin from './ParticipantLogin';
import ResetPassword from './ResetPassword';
import ParticipantProfile from './ParticipantProfile';
import Cart from './Cart';
import MerchStore from './MerchStore';
import { HomepageSkeleton, EventDetailSkeleton } from './Skeleton';
import './index.css';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import RefundPolicy from './RefundPolicy';
import ContactPage from './ContactPage';
import HelpCenter from './HelpCenter';
import DocsPage from './DocsPage';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

const RegistrationSuccess = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6 text-center">
    <div className="max-w-md w-full bg-white/[0.02] border border-white/[0.05] p-10 rounded-[3rem] backdrop-blur-3xl relative overflow-hidden">
      <div className="absolute top-0 right-0 -mr-10 -mt-10 w-32 h-32 bg-emerald-500/10 rounded-full blur-[60px]"></div>
      <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-500/20">
        <svg className="w-8 h-8 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
      </div>
      <h2 className="text-3xl font-black text-white uppercase tracking-tighter mb-4">REGISTRATION RECEIVED</h2>
      <p className="text-gray-500 text-sm leading-relaxed mb-8">Your registration has been received. The organiser will verify your payment and send your digital ticket to your email within 24 hours</p>
      <a href="/" className="inline-block bg-white text-black font-extrabold px-8 py-3 rounded-xl text-[10px] uppercase tracking-widest hover:bg-emerald-500 hover:text-white transition-all active:scale-95">Back to Home</a>
    </div>
  </div>
);

const RegistrationFail = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6 text-center">
    <div className="max-w-md w-full bg-white/[0.02] border border-white/[0.05] p-10 rounded-[3rem] backdrop-blur-3xl relative overflow-hidden">
      <div className="absolute top-0 right-0 -mr-10 -mt-10 w-32 h-32 bg-red-500/10 rounded-full blur-[60px]"></div>
      <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-red-500/20">
        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12"></path></svg>
      </div>
      <h2 className="text-3xl font-black text-white uppercase tracking-tighter mb-4">Payment Failed</h2>
      <p className="text-gray-500 text-sm leading-relaxed mb-8">We couldn't process your transaction. No data was saved. Please try again or use a different method.</p>
      <a href="/" className="inline-block bg-white text-black font-extrabold px-8 py-3 rounded-xl text-[10px] uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all active:scale-95">Try Again</a>
    </div>
  </div>
);

const ProtectedPortal = ({ children }) => {
  const token = localStorage.getItem('access_token');
  return token ? children : <Navigate to="/login" />;
};

const ProtectedProfile = ({ children }) => {
  const token = localStorage.getItem('participant_access_token');
  return token ? children : <Navigate to="/participant/login?next=/my-tickets" />;
};

// ─── AdSlot ───────────────────────────────────────────────────────────────────
const AdSlot = ({ ad, label = 'Partner', className = '' }) => {
  if (ad?.image) {
    return (
      <div className={`relative overflow-hidden rounded-2xl border border-white/[0.06] group transition-all duration-500 ${ad.url ? 'cursor-pointer hover:border-sky-500/20' : ''} ${className}`}
        onClick={() => ad.url && window.open(ad.url, '_blank', 'noopener,noreferrer')}>
        <img src={ad.image} alt={label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <span className="absolute top-2 right-3 text-[8px] font-black text-white/40 uppercase tracking-widest bg-black/40 backdrop-blur-sm px-2 py-0.5 rounded-full">Sponsored</span>
      </div>
    );
  }
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-dashed border-white/[0.04] bg-white/[0.005] py-3.5 px-5 ${className}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded-lg bg-white/[0.02] flex items-center justify-center shrink-0">
            <svg className="w-3 h-3 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
          </div>
          <p className="text-[9px] font-bold text-gray-800">{label} — contact organiser</p>
        </div>
        <span className="text-[8px] font-black text-gray-800 uppercase tracking-widest border border-white/[0.03] px-2 py-0.5 rounded-full shrink-0">Ad</span>
      </div>
    </div>
  );
};

const AdSlotSidebar = ({ ad, label = 'Official Partner' }) => {
  if (ad?.image) {
    return (
      <div className={`overflow-hidden rounded-2xl border border-white/[0.06] hover:border-sky-500/20 transition-all ${ad.url ? 'cursor-pointer' : ''}`}
        onClick={() => ad.url && window.open(ad.url, '_blank', 'noopener,noreferrer')}>
        <img src={ad.image} alt={label} className="w-full object-cover" style={{ maxHeight: 200 }} />
      </div>
    );
  }
  return (
    <div className="aspect-[3/1] bg-white/[0.02] rounded-2xl border border-dashed border-white/[0.08] flex flex-col items-center justify-center gap-1.5">
      <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
      <p className="text-[8px] text-gray-700 font-bold uppercase tracking-widest text-center px-4">Sponsor Ad Space</p>
    </div>
  );
};

// ─── Venue/Date Info Block ────────────────────────────────────────────────────
const EventInfoBlock = ({ event }) => {
  const hasVenueOrDate = event?.venue || event?.event_date;
  if (!hasVenueOrDate) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return null;
    const [h, m] = timeStr.split(':');
    const d = new Date();
    d.setHours(parseInt(h), parseInt(m));
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const dateDisplay = event.event_date
    ? event.event_end_date && event.event_end_date !== event.event_date
      ? `${formatDate(event.event_date)} — ${formatDate(event.event_end_date)}`
      : formatDate(event.event_date)
    : null;

  const timeDisplay = event.event_start_time
    ? event.event_end_time 
      ? `${formatTime(event.event_start_time)} – ${formatTime(event.event_end_time)}` 
      : formatTime(event.event_start_time)
    : null;

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-[10px] sm:text-xs font-black uppercase tracking-widest text-white/70">
      {/* Date */}
      {dateDisplay && (
        <div className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-sky-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="whitespace-nowrap">{dateDisplay}</span>
        </div>
      )}

      {/* Time */}
      {timeDisplay && (
        <div className="flex items-center gap-2 border-l border-white/10 pl-6 sm:border-none sm:pl-0">
          <svg className="w-3.5 h-3.5 text-sky-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="whitespace-nowrap">{timeDisplay}</span>
        </div>
      )}

      {/* Venue */}
      {event.venue && (
        <div className="flex items-center gap-2 w-full sm:w-auto sm:border-l sm:border-white/10 sm:pl-6">
          <svg className="w-3.5 h-3.5 text-sky-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="truncate max-w-xs">{event.venue}</span>
        </div>
      )}
    </div>
  );
};
// ─── Segment Card with Perks ──────────────────────────────────────────────────
const SegmentCard = ({ seg, onBook }) => {
  const isExpired = seg.reg_deadline && new Date() > new Date(seg.reg_deadline);
  const deadline = seg.reg_deadline ? new Date(seg.reg_deadline) : null;
  const daysLeft = deadline ? Math.max(0, Math.ceil((deadline - new Date()) / (1000 * 60 * 60 * 24))) : null;
  const [showPerks, setShowPerks] = useState(false);
  const perks = Array.isArray(seg.perks) ? seg.perks.filter(Boolean) : [];
  return (
    <div className={`group relative bg-white/[0.03] rounded-3xl border overflow-hidden transition-all duration-500 ${isExpired ? 'border-white/5 opacity-70' : 'border-white/[0.07] hover:border-sky-500/30 hover:bg-white/[0.05]'}`}>
      {seg.banner && (<div className="h-36 overflow-hidden relative"><img src={seg.banner} alt={seg.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /><div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60 pointer-events-none" /></div>)}
      <div className="p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 className="text-sm sm:text-base font-black text-white uppercase tracking-tight leading-tight">{seg.title}</h3>
            {seg.access_note && <p className="text-[10px] font-black text-sky-400 uppercase tracking-[0.2em] mt-1 italic">{seg.access_note}</p>}
          </div>
          <div className="shrink-0 text-right"><span className="block text-base sm:text-lg font-black text-white">{seg.fee}</span><span className="text-[9px] font-bold text-gray-500 uppercase">BDT</span></div>
        </div>
        {seg.description && <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-3">{seg.description}</p>}
        {perks.length > 0 && (
          <div className="mb-4">
            <button type="button" onClick={(e) => { e.stopPropagation(); setShowPerks(v => !v); }} className="flex items-center gap-1.5 text-[9px] font-black text-sky-500 uppercase tracking-widest hover:text-sky-300 transition-colors">
              <svg className={`w-3 h-3 transition-transform duration-200 ${showPerks ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" /></svg>
              {showPerks ? 'Hide' : "What's Included"}
            </button>
            {showPerks && (<ul className="mt-2.5 space-y-1.5 animate-in slide-in-from-top-2 duration-200">{perks.map((perk, i) => (<li key={i} className="flex items-start gap-2"><svg className="w-3 h-3 text-emerald-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" /></svg><span className="text-[11px] text-gray-300 leading-tight">{perk}</span></li>))}</ul>)}
          </div>
        )}
        <div className="flex items-center justify-between">
          {daysLeft !== null && !isExpired && (<span className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border ${daysLeft <= 3 ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-white/5 border-white/10 text-gray-500'}`}>{daysLeft === 0 ? 'Closes today' : `${daysLeft}d left`}</span>)}
          {isExpired && <span className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-red-500">Closed</span>}
          {daysLeft === null && !isExpired && <span />}
          {isExpired ? <span className="text-[9px] font-black text-red-500 uppercase tracking-widest">Closed</span> : (<button onClick={() => onBook(seg)} className="flex items-center gap-1.5 text-sky-400 hover:gap-3 transition-all duration-300 active:scale-95"><span className="text-[10px] font-black uppercase tracking-widest">Register</span><span className="text-sm">→</span></button>)}
        </div>
      </div>
    </div>
  );
};

// ─── Add to Calendar + Share Buttons ─────────────────────────────────────────
const EventActionButtons = ({ event }) => {
  const [shareCopied, setShareCopied] = useState(false);
  const eventUrl = `https://api.eventdeckapp.com/api/share/event/${event.id}/`;

  const buildCalendarUrl = () => {
    if (!event?.event_date) return null;
    const startDate = event.event_date.replace(/-/g, '');
    const endDate = (event.event_end_date || event.event_date).replace(/-/g, '');
    const startTime = event.event_start_time ? event.event_start_time.replace(/:/g, '').substring(0, 4) : '0900';
    const endTime = event.event_end_time ? event.event_end_time.replace(/:/g, '').substring(0, 4) : '1800';
    const start = `${startDate}T${startTime}00`;
    const end = `${endDate}T${endTime}00`;
    const title = encodeURIComponent(event.name || '');
    const details = encodeURIComponent(event.description || '');
    const location = encodeURIComponent(event.venue || '');
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}`;
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(eventUrl);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  const calendarUrl = buildCalendarUrl();

  return (
    <div className="flex flex-wrap gap-2 mt-5">
      {calendarUrl && (
        <a
          href={calendarUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 bg-black/40 backdrop-blur-xl border border-white/10 hover:border-sky-500/30 px-3.5 py-2 rounded-full transition-all group"
        >
          <svg className="w-3 h-3 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="text-[10px] font-black text-white/70 group-hover:text-white uppercase tracking-widest">
            Add to Calendar
          </span>
        </a>
      )}

      {event?.whatsapp_url && (
        <a
          href={event.whatsapp_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 bg-black/40 backdrop-blur-xl border border-white/10 hover:border-green-500/30 px-3.5 py-2 rounded-full transition-all group"
        >
          <span className="text-green-400 text-xs">●</span>
          <span className="text-[10px] font-black text-white/70 group-hover:text-white uppercase tracking-widest">
            WhatsApp
          </span>
        </a>
      )}

      {event?.facebook_url && (
        <a
          href={event.facebook_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 bg-black/40 backdrop-blur-xl border border-white/10 hover:border-blue-500/30 px-3.5 py-2 rounded-full transition-all group"
        >
          <span className="text-blue-400 text-xs">●</span>
          <span className="text-[10px] font-black text-white/70 group-hover:text-white uppercase tracking-widest">
            Facebook
          </span>
        </a>
      )}

      {event?.instagram_url && (
        <a
          href={event.instagram_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 bg-black/40 backdrop-blur-xl border border-white/10 hover:border-pink-500/30 px-3.5 py-2 rounded-full transition-all group"
        >
          <span className="text-pink-400 text-xs">●</span>
          <span className="text-[10px] font-black text-white/70 group-hover:text-white uppercase tracking-widest">
            Instagram
          </span>
        </a>
      )}

      {event?.website_url && (
        <a
          href={event.website_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 bg-black/40 backdrop-blur-xl border border-white/10 hover:border-white/30 px-3.5 py-2 rounded-full transition-all group"
        >
          <span className="text-white/60 text-xs">●</span>
          <span className="text-[10px] font-black text-white/70 group-hover:text-white uppercase tracking-widest">
            Website
          </span>
        </a>
      )}

      <button
        onClick={handleCopyLink}
        className="flex items-center gap-1.5 bg-black/40 backdrop-blur-xl border border-white/10 hover:border-white/20 px-3.5 py-2 rounded-full transition-all group"
      >
        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        <span className="text-[10px] font-black text-white/70 group-hover:text-white uppercase tracking-widest">
          {shareCopied ? 'Copied!' : 'Copy Link'}
        </span>
      </button>
    </div>
  );
};

// ─── Organiser Profile Block ──────────────────────────────────────────────────
const OrganiserBlock = ({ event }) => {
  const multiOrgs = Array.isArray(event?.organisers) && event.organisers.length > 0;
  const hasSingleOrg = event?.organiser_name || event?.organiser_logo;
  if (!multiOrgs && !hasSingleOrg) return null;

  if (multiOrgs) {
    return (
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-3xl p-5 space-y-3">
        <p className="text-[8px] font-black text-gray-600 uppercase tracking-widest">Organised By</p>
        {event.organisers.sort((a, b) => a.order - b.order).map(org => (
          <div key={org.id} className="flex items-center gap-3">
            {org.logo_url && (
              <img src={org.logo_url} alt={org.name} className="w-12 h-12 object-contain bg-white rounded-xl p-1.5 shrink-0" />
            )}
            <div className="min-w-0">
              <h4 className="text-sm font-black text-white uppercase tracking-tight">{org.name}</h4>
              {org.bio && <p className="text-gray-500 text-xs leading-relaxed mt-1 line-clamp-3">{org.bio}</p>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-3xl p-5 space-y-3">
      <p className="text-[8px] font-black text-gray-600 uppercase tracking-widest">Organised By</p>
      <div className="flex items-center gap-3">
        {event.organiser_logo && (
          <img src={event.organiser_logo} alt={event.organiser_name} className="w-12 h-12 object-contain bg-white rounded-xl p-1.5 shrink-0" />
        )}
        <div className="min-w-0">
          <h4 className="text-sm font-black text-white uppercase tracking-tight">{event.organiser_name}</h4>
          {event.organiser_bio && <p className="text-gray-500 text-xs leading-relaxed mt-1 line-clamp-3">{event.organiser_bio}</p>}
        </div>
      </div>
    </div>
  );
};

// ─── Refund & Transfer Policy Block ──────────────────────────────────────────
const PolicyBlock = ({ event }) => {
  const [open, setOpen] = useState(false);
  if (!event?.refund_policy && !event?.is_transferable && !event?.transfer_conditions) return null;
  return (
    <div className="bg-white/[0.02] border border-white/[0.05] rounded-3xl overflow-hidden">
      <button onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between p-5 hover:bg-white/[0.02] transition-all">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Terms & Policies</span>
        </div>
        <svg className={`w-4 h-4 text-gray-600 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-white/[0.05] pt-4 animate-in fade-in duration-200">
          {event.refund_policy && (
            <div>
              <p className="text-[9px] font-black text-gray-600 uppercase tracking-widest mb-1.5">Refund Policy</p>
              <p className="text-xs text-gray-400 leading-relaxed">{event.refund_policy}</p>
            </div>
          )}
          {(event.is_transferable !== undefined) && (
            <div>
              <p className="text-[9px] font-black text-gray-600 uppercase tracking-widest mb-1.5">Ticket Transfer</p>
              <div className="flex items-center gap-2">
                <span className={`text-[9px] font-black uppercase px-2.5 py-1 rounded-full border ${event.is_transferable ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
                  {event.is_transferable ? 'Transferable' : 'Non-transferable'}
                </span>
              </div>
              {event.is_transferable && event.transfer_conditions && (
                <p className="text-xs text-gray-400 leading-relaxed mt-2">{event.transfer_conditions}</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Page transition wrapper ──────────────────────────────────────────────────
const PageView = ({ children, viewKey }) => {
  const prevKey = useRef(viewKey);
  const isNew = prevKey.current !== viewKey;
  useEffect(() => { prevKey.current = viewKey; }, [viewKey]);
  return (<div key={viewKey} className={isNew ? 'animate-in fade-in slide-in-from-bottom-4 duration-400' : ''}>{children}</div>);
};

// ─── Hero Carousel ────────────────────────────────────────────────────────────
const HeroCarousel = ({ events, onSelectEvent }) => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const timerRef = useRef(null);

  const goTo = (index) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrent(index);
    setTimeout(() => setIsAnimating(false), 600);
  };

  const prev = () => goTo((current - 1 + events.length) % events.length);
  const next = () => goTo((current + 1) % events.length);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCurrent(c => (c + 1) % events.length);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, [events.length]);

  const resetTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent(c => (c + 1) % events.length);
    }, 5000);
  };

  const handlePrev = () => { prev(); resetTimer(); };
  const handleNext = () => { next(); resetTimer(); };

  const event = events[current];

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    /* FIXED RATIO: aspect-[16/9] for mobile ensures landscape, md:aspect-[21/9] for ultra-wide on PC */
    <div className="relative w-full overflow-hidden rounded-[2rem] mt-24 sm:mt-28 mb-10 aspect-[16/9] md:aspect-[21/9] shadow-2xl group">
      
      {/* Background Slides */}
      {events.map((ev, idx) => (
        <div key={ev.id}
          className={`absolute inset-0 transition-all duration-1000 ease-in-out ${idx === current ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-105 z-0'}`}>
          {ev.banner ? (
            <img src={ev.banner} alt={ev.name} className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-sky-900/40 to-[#050505]" />
          )}
          {/* Enhanced gradients for readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent hidden md:block" />
        </div>
      ))}

      {/* Content Layer */}
      <div className="absolute inset-0 z-20 flex flex-col justify-end p-5 sm:p-12 pb-6 sm:pb-10">
        <div className={`transition-all duration-500 max-w-5xl w-full flex flex-row items-end justify-between gap-4 ${isAnimating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
          
          {/* Left Side: Text Info */}
          <div className="flex-1 min-w-0">
            {event?.category && event.category !== 'other' && (
              <span className="inline-block bg-sky-500/20 border border-sky-500/30 text-sky-400 text-[7px] sm:text-[9px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-[0.2em] mb-2 sm:mb-3">
                {event.category}
              </span>
            )}
            <h2 className="text-xl sm:text-4xl lg:text-5xl font-black text-white uppercase tracking-tighter leading-[0.95] mb-2 sm:mb-3 max-w-[700px] break-words">
              {event?.name}
            </h2>
            <div className="flex flex-wrap gap-x-4 gap-y-1 items-center">
              {event?.event_date && (
                <div className="flex items-center gap-1 text-white/50 text-[8px] sm:text-xs font-bold uppercase tracking-wider">
                  <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  {formatDate(event.event_date)}
                </div>
              )}
              {event?.venue && (
                <div className="flex items-center gap-1 text-white/50 text-[8px] sm:text-xs font-bold uppercase tracking-wider">
                  <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>
                  {event.venue}
                </div>
              )}
            </div>
          </div>

          {/* Right Side: Smaller Explore Button */}
          <div className="shrink-0 mb-1 sm:mb-0">
            <button
              // Remove the ', navigate' part from inside the brackets
              onClick={() => onSelectEvent(event)}
              className="group/btn relative overflow-hidden bg-sky-500 hover:bg-white text-black font-black px-4 py-2 sm:px-5 sm:py-2.5 rounded-xl text-[8px] sm:text-[10px] uppercase tracking-[0.15em] transition-all active:scale-95 shadow-xl whitespace-nowrap"
            >
              <span className="relative z-10 flex items-center gap-1.5">
                Explore Event <span className="text-xs sm:text-sm group-hover/btn:translate-x-1 transition-transform">→</span>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <div className="absolute inset-y-0 left-0 right-0 z-30 flex items-center justify-between px-4 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <button onClick={handlePrev}
          className="w-8 h-8 sm:w-10 sm:h-10 bg-black/40 backdrop-blur-md border border-white/10 rounded-full flex items-center justify-center text-white hover:bg-sky-500 hover:text-black transition-all pointer-events-auto">
          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 19l-7-7 7-7" /></svg>
        </button>
        <button onClick={handleNext}
          className="w-8 h-8 sm:w-10 sm:h-10 bg-black/40 backdrop-blur-md border border-white/10 rounded-full flex items-center justify-center text-white hover:bg-sky-500 hover:text-black transition-all pointer-events-auto">
          <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>

      {/* Indicators */}
      <div className="absolute top-4 right-6 sm:top-6 sm:right-8 z-30 flex gap-1 sm:gap-1.5">
        {events.map((_, idx) => (
          <button key={idx} onClick={() => { goTo(idx); resetTimer(); }}
            className={`transition-all duration-500 rounded-full ${idx === current ? 'w-6 sm:w-8 h-0.5 sm:h-1 bg-sky-500' : 'w-1.5 sm:w-2 h-0.5 sm:h-1 bg-white/20'}`} />
        ))}
      </div>
    </div>
  );
};
const StaffPage = () => {
    const [authed, setAuthed] = useState(false);
    const [pin, setPin] = useState('');

    useEffect(() => {
        if (localStorage.getItem('staff_pin')) setAuthed(true);
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!pin) return;
        localStorage.setItem('staff_pin', pin);
        setAuthed(true);
    };

    if (!authed) return (
        <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
            <div className="w-full max-w-sm">
                <div className="text-center mb-10">
            <img
              src="/logo192.png"
              alt="EventDeck"
              className="w-14 h-14 object-contain mx-auto mb-4"
            />
                    <h1 className="text-2xl font-black text-white uppercase tracking-tight">Gate Scanner</h1>
                    <p className="text-gray-500 text-sm mt-2">Enter your gate PIN to continue</p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                        type="password"
                        value={pin}
                        onChange={e => setPin(e.target.value)}
                        placeholder="Enter Gate PIN"
                        className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-5 py-4 text-white text-center text-2xl font-black tracking-[0.4em] outline-none focus:border-sky-500 transition-all"
                        autoFocus
                    />
                    <button type="submit"
                        className="w-full bg-sky-500 text-black font-black py-4 rounded-2xl text-sm uppercase tracking-widest hover:bg-sky-400 transition-all">
                        Enter Scanner →
                    </button>
                </form>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#050505] py-10 px-4">
            <div className="max-w-xl mx-auto mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
            <img
              src="/logo192.png"
              alt="EventDeck"
              className="w-8 h-8 object-contain"
            />
                    <div>
                        <p className="text-xs font-black text-white uppercase tracking-widest">Gate Scanner</p>
                        <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest">● Active</p>
                    </div>
                </div>
                <button
                    onClick={() => { localStorage.removeItem('staff_pin'); setAuthed(false); setPin(''); }}
                    className="text-[10px] font-black text-gray-600 hover:text-white uppercase tracking-widest border border-white/10 px-3 py-2 rounded-xl transition-all">
                    Exit
                </button>
            </div>
            <Scanner />
        </div>
    );
};

// ─── CATEGORY FILTER DEFINITIONS ─────────────────────────────────────────────
const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'competition', label: 'Competition' },
  { key: 'conference', label: 'Conference' },
  { key: 'workshop', label: 'Workshop' },
  { key: 'hackathon', label: 'Hackathon' },
  { key: 'festival', label: 'Festival' },
  { key: 'sports', label: 'Sports' },
  { key: 'seminar', label: 'Seminar' },
  { key: 'concert', label: 'Concert' },
  { key: 'exhibition', label: 'Exhibition' },
  { key: 'other', label: 'Other' },
];
const AutoFitEventTitle = ({ title }) => {
  const ref = React.useRef(null);
  const [size, setSize] = React.useState(72);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const resize = () => {
      let fontSize = window.innerWidth < 640 ? 34 : 72;
      const min = 20;

      el.style.fontSize = fontSize + "px";

      while (el.scrollHeight > el.clientHeight && fontSize > min) {
        fontSize -= 2;
        el.style.fontSize = fontSize + "px";
      }

      setSize(fontSize);
    };

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [title]);

  return (
    <h1
      ref={ref}
      className="font-black text-white uppercase tracking-tight leading-[0.95] max-w-[1100px] overflow-hidden"
      style={{
        fontSize: size,
        maxHeight: size * 2 * 0.95,
      }}
    >
      {title}
    </h1>
  );
};

function App() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isStaffMode, setIsStaffMode] = useState(false);
  const [staffView, setStaffView] = useState('scanner');
  const [eventLoading, setEventLoading] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);

  const [homepageAd1, setHomepageAd1] = useState(null);
  const [homepageAd2, setHomepageAd2] = useState(null);
  const [eventAd1, setEventAd1] = useState(null);
  const [eventAd2, setEventAd2] = useState(null);

  

  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [eventTab, setEventTab] = useState('segments');
  const [merchItems, setMerchItems] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  const [participantUser, setParticipantUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem('participant_user') || 'null'); } catch { return null; }
  });

  const participantToken = localStorage.getItem('participant_access_token');

  useEffect(() => {
    setEventsLoading(true);
    axios.get(`${API_BASE}/api/events/`)
      .then(res => {
        setEvents(res.data);
        setEventsLoading(false);
        if (res.data.length > 0) {
          axios.get(`${API_BASE}/api/events/${res.data[0].id}/ads/`)
            .then(adsRes => {
              setHomepageAd1(adsRes.data.homepage_ad_1?.image ? adsRes.data.homepage_ad_1 : null);
              setHomepageAd2(adsRes.data.homepage_ad_2?.image ? adsRes.data.homepage_ad_2 : null);
            }).catch(() => {});
        }
      })
      .catch(err => { console.error(err); setEventsLoading(false); });
  }, []);
  // Add this block starting at Line 612
  useEffect(() => {
    //Only run this if we have events but haven't selected one yet
    const path = window.location.pathname;
    if (path.startsWith('/event/') && events.length > 0 && !selectedEvent) {
      const eventId = path.split('/')[2];
      const foundEvent = events.find(e => e.id.toString() === eventId);

      if (foundEvent) {
        // This opens the event and updates the UI
        handleSelectEvent(foundEvent);
      }
    }
  }, [events, selectedEvent]); // Added selectedEvent to the dependency array
  const handleSelectEvent = (event) => {
    // FIX: Only navigate if we aren't already on this event's page
    if (window.location.pathname !== `/event/${event.id}`) {
      navigate(`/event/${event.id}`);
    }
    setSelectedEvent(event);
    setSelectedSegment(null);
    
    setEventAd1(null); setEventAd2(null);
    setEventLoading(true);
    setEventTab('segments');
    setMerchItems([]);

    axios.get(`${API_BASE}/api/events/${event.id}/`)
      .then(res => { setSelectedEvent(res.data); setEventLoading(false); })
      .catch(() => { setSelectedEvent(event); setEventLoading(false); });

    axios.get(`${API_BASE}/api/events/${event.id}/ads/`)
      .then(res => {
        setEventAd1(res.data.event_ad_1?.image ? res.data.event_ad_1 : null);
        setEventAd2(res.data.event_ad_2?.image ? res.data.event_ad_2 : null);
      }).catch(() => { setEventAd1(null); setEventAd2(null); });

    axios.get(`${API_BASE}/api/events/${event.id}/merch/`)
      .then(res => setMerchItems(res.data))
      .catch(() => setMerchItems([]));
  };

  const handleStaffToggle = () => {
    if (isStaffMode) { setIsStaffMode(false); localStorage.removeItem('staff_pin'); }
    else {
      const pin = prompt("ENTER SYSTEM PIN:");
      if (pin) { localStorage.setItem('staff_pin', pin); setIsStaffMode(true); }
      else { alert("ACCESS DENIED."); }
    }
  };

  const handleSuccess = () => {
    setShowSuccess(true);
    setSelectedSegment(null);
    setSelectedEvent(null);
  };

  const handleParticipantLogout = () => {
    localStorage.removeItem('participant_access_token');
    localStorage.removeItem('participant_refresh_token');
    localStorage.removeItem('participant_user');
    setParticipantUser(null);
  };
  const handleGoogleLoginSuccess = async (credentialResponse) => {
    try {
      const res = await axios.post(`${API_BASE}/api/auth/google/`, {
        token: credentialResponse.credential
      });
      localStorage.setItem('participant_access_token', res.data.access);
      localStorage.setItem('participant_user', JSON.stringify(res.data.user));
      setParticipantUser(res.data.user);
      window.location.reload();
    } catch (err) {
      console.error("Google Auth Error:", err);
      alert("Registration/Login failed. Please try again.");
    }
  };

  const filteredEvents = events.filter(e => {
    const matchesCategory = activeCategory === 'all' || e.category === activeCategory;
    const matchesSearch = !searchQuery || e.name?.toLowerCase().includes(searchQuery.toLowerCase()) || e.description?.toLowerCase().includes(searchQuery.toLowerCase()) || e.venue?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const viewKey = selectedSegment ? 'registration' : selectedEvent ? 'event' : 'home';

  const renderRegistration = () => (
    <div className="max-w-7xl mx-auto px-4 md:px-6">
      <div className="flex items-center gap-1.5 flex-wrap mb-6 sm:mb-8 pt-6 sm:pt-8">
        <button
          onClick={() => {
            setSelectedEvent(null);
            setSelectedSegment(null);
            navigate('/');
          }}
          className="text-gray-600 hover:text-gray-400 text-[10px] font-black uppercase tracking-widest transition-colors"
        >
          Events
        </button>
        <span className="text-gray-700 text-xs">›</span>
        <button
          onClick={() => {
            setSelectedSegment(null);
            if (selectedEvent?.id) {
              navigate(`/event/${selectedEvent.id}`);
            }
          }}
          className="text-gray-600 hover:text-gray-400 text-[10px] font-black uppercase tracking-widest transition-colors truncate max-w-[120px] sm:max-w-none"
        >
          {selectedEvent?.name}
        </button>
        <span className="text-gray-700 text-xs">›</span>
        <span className="text-sky-500 text-[10px] font-black uppercase tracking-widest truncate max-w-[120px] sm:max-w-xs">{selectedSegment?.title}</span>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 pb-20">
        <div className="lg:col-span-2">
          <RegistrationForm segment={selectedSegment} onSuccess={handleSuccess} />
        </div>
        <div className="space-y-5 order-first lg:order-last">
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-3xl overflow-hidden">
            {selectedEvent?.banner && <img src={selectedEvent.banner} alt={selectedEvent.name} className="w-full h-28 sm:h-32 object-cover" />}
            <div className="p-4 sm:p-5">
              <p className="text-[9px] font-black text-sky-500 uppercase tracking-widest mb-1">Event</p>
              <h4 className="text-sm sm:text-base font-black text-white uppercase tracking-tight">{selectedEvent?.name}</h4>
              {(selectedEvent?.event_date || selectedEvent?.venue) && (
                <div className="mt-3 space-y-1.5">
                  {selectedEvent.event_date && (<div className="flex items-center gap-2"><svg className="w-3 h-3 text-sky-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg><span className="text-[10px] text-gray-400">{new Date(selectedEvent.event_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span></div>)}
                  {selectedEvent.venue && (<div className="flex items-center gap-2"><svg className="w-3 h-3 text-sky-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg><span className="text-[10px] text-gray-400 truncate">{selectedEvent.venue}</span></div>)}
                </div>
              )}
            </div>
          </div>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-3xl p-4">
            <p className="text-[8px] font-black text-gray-700 uppercase tracking-[0.3em] mb-3">Official Partner</p>
            <AdSlotSidebar ad={eventAd2} />
            <p className="text-[8px] text-gray-700 text-center mt-2 uppercase tracking-widest font-bold">Sponsored</p>
          </div>
          {selectedEvent?.segments?.filter(s => s.id !== selectedSegment?.id).length > 0 && (
            <div className="bg-white/[0.02] border border-white/[0.05] rounded-2xl p-4 sm:p-5">
              <p className="text-[9px] font-black text-gray-600 uppercase tracking-widest mb-3">Other Segments</p>
              <div className="space-y-2">
                {selectedEvent.segments.filter(s => s.id !== selectedSegment?.id).slice(0, 3).map(s => {
                  const expired = s.reg_deadline && new Date() > new Date(s.reg_deadline);
                  return (<button key={s.id} disabled={expired} onClick={() => !expired && setSelectedSegment(s)} className={`w-full flex items-center justify-between p-2.5 rounded-xl border text-left transition-all ${expired ? 'border-white/5 opacity-40 cursor-not-allowed' : 'border-white/5 hover:border-sky-500/30 hover:bg-white/[0.03]'}`}><span className="text-xs font-bold text-white truncate">{s.title}</span><span className="text-[9px] font-black text-gray-500 shrink-0 ml-2">{s.fee} BDT</span></button>);
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderEventDetail = () => {
    if (eventLoading) return <EventDetailSkeleton />;
    const allSegments = selectedEvent?.segments || [];
    const totalSegments = allSegments.length;
    const openSegments = allSegments.filter(s => !s.reg_deadline || new Date() <= new Date(s.reg_deadline)).length;

    return (
      <div>
        <div className="relative w-full h-[520px] sm:h-[560px] lg:h-[480px] xl:h-[520px] overflow-hidden rounded-b-[2rem] sm:rounded-b-[3rem] shadow-2xl">
          {selectedEvent?.banner ? (<>
            <img src={selectedEvent.banner} alt={selectedEvent.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
          </>) : <div className="absolute inset-0 bg-gradient-to-br from-sky-900/20 via-[#050505] to-[#050505]" />}

          {/* Back Button Overlay */}
          <button
            onClick={() => {
              setSelectedEvent(null);
              setSelectedSegment(null);
              navigate('/');
            }}
            className="absolute top-28 sm:top-32 lg:top-24 left-5 sm:left-6 z-20 w-10 h-10 bg-black/40 backdrop-blur-xl border border-white/10 rounded-full flex items-center justify-center text-white hover:bg-sky-500 hover:text-black transition-all"
          >
            ←
          </button>
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-24 sm:pt-0 pb-6 sm:pb-10 flex flex-col justify-end h-full">
            <div className="max-w-4xl">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="bg-sky-500 text-black text-[7px] sm:text-[9px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest">Active</span>
                {selectedEvent?.category && selectedEvent.category !== 'other' && (
                  <span className="bg-white/10 text-white/60 text-[7px] sm:text-[9px] font-black px-2.5 py-1 rounded-full uppercase tracking-widest capitalize">{selectedEvent.category}</span>
                )}
              </div>
              {/* Scaled Text: text-2xl on mobile, text-7xl on PC */}
              <h1
                className="font-black text-white uppercase tracking-tighter leading-[0.92] max-w-[1050px] drop-shadow-[0_10px_30px_rgba(0,0,0,0.65)]"
                style={{
                  fontSize:
                    selectedEvent?.name?.length > 70
                      ? "clamp(30px, 6.8vw, 56px)"
                      : selectedEvent?.name?.length > 45
                        ? "clamp(34px, 7.2vw, 64px)"
                        : "clamp(42px, 8vw, 76px)",
                }}
              >
                {selectedEvent?.name}
              </h1>
              <EventInfoBlock event={selectedEvent} />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-20">
          {/* Action Buttons Row (Now visible on mobile below banner) */}
          <div className="mt-6">
            <EventActionButtons event={selectedEvent} />
          </div>

          {/* Description with "See Details" Toggle */}
          {selectedEvent?.description && (
            <div className="mt-8 mb-10 bg-white/[0.02] border border-white/[0.05] rounded-[2rem] p-6 sm:p-8">
              <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-4">About Event</p>
              <div className={`text-sm sm:text-base text-gray-400 leading-relaxed transition-all duration-500 ${descExpanded ? '' : 'line-clamp-2'}`}>
                {selectedEvent.description}
              </div>
              <button
                onClick={() => setDescExpanded(!descExpanded)}
                className="mt-4 flex items-center gap-2 text-[10px] font-black text-sky-500 uppercase tracking-widest hover:text-sky-400 transition-colors group"
              >
                {descExpanded ? '↑ Hide Details' : '↓ See Full Details'}
                <span className="w-4 h-4 rounded-full bg-sky-500/10 flex items-center justify-center group-hover:bg-sky-500/20 transition-colors">
                  {descExpanded ? '−' : '+'}
                </span>
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
            <div className="lg:col-span-2 space-y-5">
              <div className="flex gap-2">
                <button onClick={() => setEventTab('segments')}
                  className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${eventTab === 'segments' ? 'bg-sky-500 text-black' : 'bg-white/5 text-gray-500 border border-white/5 hover:text-white'}`}>
                  Segments
                </button>
                {merchItems.length > 0 && (
                  <button onClick={() => setEventTab('merch')}
                    className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${eventTab === 'merch' ? 'bg-sky-500 text-black' : 'bg-white/5 text-gray-500 border border-white/5 hover:text-white'}`}>
                    Merch {merchItems.length > 0 && <span className="ml-1">({merchItems.length})</span>}
                  </button>
                )}
              </div>
              {eventTab === 'segments' && (
                <>
                  <div>
                    <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.4em] mb-2">Competition Categories</p>
                    <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">Select Your Segment</h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {allSegments.map(seg => <SegmentCard key={seg.id} seg={seg} onBook={(s) => setSelectedSegment(s)} />)}
                  </div>
                  {allSegments.length === 0 && (<div className="p-16 text-center border border-dashed border-white/5 rounded-3xl"><p className="text-gray-600 font-black uppercase tracking-widest text-xs">No active segments at this time.</p></div>)}
                </>
              )}
              {eventTab === 'merch' && (
                <MerchStore items={merchItems} eventName={selectedEvent?.name} onOrderSuccess={() => {}} />
              )}
            </div>

            <div className="space-y-5">
              <div className="bg-white/[0.03] border border-white/[0.07] rounded-3xl p-5 sm:p-6 space-y-4">
                <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest">Event At a Glance</p>
                <div className="grid grid-cols-2 gap-4">
                  <div><p className="text-xl sm:text-2xl font-black text-white">{totalSegments}</p><p className="text-[9px] text-gray-500 uppercase font-bold tracking-widest">Segments</p></div>
                  <div><p className="text-xl sm:text-2xl font-black text-sky-400">{openSegments}</p><p className="text-[9px] text-gray-500 uppercase font-bold tracking-widest">Open Now</p></div>
                </div>
                {selectedEvent?.venue && (<div className="pt-3 border-t border-white/[0.05]"><p className="text-[8px] font-black text-gray-600 uppercase tracking-widest mb-1.5">Venue</p><p className="text-xs text-gray-400 leading-relaxed">{selectedEvent.venue}</p></div>)}
                {selectedEvent?.event_date && (
                  <div className={selectedEvent?.venue ? '' : 'pt-3 border-t border-white/[0.05]'}>
                    <p className="text-[8px] font-black text-gray-600 uppercase tracking-widest mb-1.5">Date</p>
                    <p className="text-xs text-gray-400">{new Date(selectedEvent.event_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}{selectedEvent.event_end_date && selectedEvent.event_end_date !== selectedEvent.event_date && (<> — {new Date(selectedEvent.event_end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</>)}</p>
                  </div>
                )}
              </div>
              <OrganiserBlock event={selectedEvent} />
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-3xl p-4">
                <p className="text-[8px] font-black text-gray-700 uppercase tracking-[0.3em] mb-3">Official Partner</p>
                <AdSlotSidebar ad={eventAd2} />
                <p className="text-[8px] text-gray-700 text-center mt-2 uppercase tracking-widest font-bold">Sponsored</p>
              </div>
              {allSegments.some(s => s.reg_deadline) && (
                <div className="bg-white/[0.02] border border-white/[0.05] rounded-3xl p-5 sm:p-6">
                  <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-4">Registration Deadlines</p>
                  <div className="space-y-3">
                    {allSegments.filter(s => s.reg_deadline).map(s => {
                      const d = new Date(s.reg_deadline); const expired = new Date() > d;
                      return (<div key={s.id} className="flex items-center justify-between gap-2"><span className="text-xs font-bold text-white truncate">{s.title}</span><span className={`text-[9px] font-black uppercase tracking-widest shrink-0 ${expired ? 'text-red-500' : 'text-gray-400'}`}>{expired ? 'Closed' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span></div>);
                    })}
                  </div>
                </div>
              )}
              <PolicyBlock event={selectedEvent} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderHomepage = () => {
    if (eventsLoading) return <HomepageSkeleton />;

    const featuredEvents = events
      .filter(e => e.show_in_carousel)
      .sort((a, b) => a.carousel_order - b.carousel_order);

    const spotlightEvent = filteredEvents[0] || events[0] || null;

    const formatDate = (dateStr) => {
      if (!dateStr) return 'Date TBA';
      return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    };

    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* HERO */}
        {featuredEvents.length > 0 ? (
          <HeroCarousel events={featuredEvents} onSelectEvent={handleSelectEvent} />
        ) : (
          <section className="pt-24 sm:pt-32 pb-12 md:pb-20">
            <div className="relative overflow-hidden rounded-[2.5rem] border border-white/[0.07] bg-[radial-gradient(circle_at_top_left,rgba(56,189,248,0.14),transparent_28%),linear-gradient(135deg,#07111d_0%,#050505_70%)] px-6 py-14 sm:px-10 sm:py-20 shadow-[0_20px_80px_rgba(0,0,0,0.45)]">
              <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_bottom_right,rgba(6,182,212,0.08),transparent_28%)]" />
                <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-28 sm:pt-32 lg:pt-24 pb-8 sm:pb-10 flex flex-col justify-end h-full">
                <div className="inline-flex items-center gap-2 rounded-full border border-sky-500/20 bg-sky-500/10 px-4 py-1.5 mb-6">
                  <span className="w-2 h-2 rounded-full bg-sky-400" />
                  <span className="text-[9px] font-black text-sky-400 uppercase tracking-[0.35em]">
                    Premium Event Infrastructure
                  </span>
                </div>

                <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-white tracking-tighter leading-[0.92] max-w-4xl">
                  Engineering events,
                  <br />
                  <span className="text-white/20">elevated.</span>
                </h1>

                <p className="mt-6 max-w-2xl text-sm sm:text-lg text-gray-400 leading-relaxed">
                  Register, verify, and issue professional QR tickets for summits, competitions, workshops, and team-based engineering events.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    onClick={() => {
                      const target = document.getElementById('events-grid');
                      if (target) target.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="bg-white text-black hover:bg-sky-400 font-black px-6 py-3 rounded-2xl text-[10px] uppercase tracking-[0.25em] transition-all active:scale-95"
                  >
                    Explore Events
                  </button>

                  <button
                    onClick={() => navigate('/participant/login')}
                    className="border border-white/10 bg-white/[0.03] hover:border-sky-500/30 hover:bg-white/[0.05] text-white font-black px-6 py-3 rounded-2xl text-[10px] uppercase tracking-[0.25em] transition-all active:scale-95"
                  >
                    My Tickets
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* TRUST / STATS */}
        <section className="mb-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="rounded-[1.5rem] border border-white/[0.06] bg-white/[0.025] p-5">
            <p className="text-[9px] font-black text-gray-600 uppercase tracking-[0.25em] mb-2">Events Live</p>
            <p className="text-2xl sm:text-3xl font-black text-white">{events.length}</p>
          </div>
          <div className="rounded-[1.5rem] border border-white/[0.06] bg-white/[0.025] p-5">
            <p className="text-[9px] font-black text-gray-600 uppercase tracking-[0.25em] mb-2">Categories</p>
            <p className="text-2xl sm:text-3xl font-black text-sky-400">
              {new Set(events.map(e => e.category).filter(Boolean)).size}
            </p>
          </div>
          <div className="rounded-[1.5rem] border border-white/[0.06] bg-white/[0.025] p-5">
            <p className="text-[9px] font-black text-gray-600 uppercase tracking-[0.25em] mb-2">Open Listings</p>
            <p className="text-2xl sm:text-3xl font-black text-white">{filteredEvents.length}</p>
          </div>
          <div className="rounded-[1.5rem] border border-white/[0.06] bg-white/[0.025] p-5">
            <p className="text-[9px] font-black text-gray-600 uppercase tracking-[0.25em] mb-2">Platform Focus</p>
            <p className="text-sm sm:text-base font-black text-sky-400 uppercase tracking-tight">
              Team + QR
            </p>
          </div>
        </section>

        <AdSlot ad={homepageAd1} label="Headline Sponsor" className="mb-10" />

        {/* SEARCH + FILTERS */}
        <section className="mb-10 rounded-[2rem] border border-white/[0.06] bg-white/[0.02] p-4 sm:p-5 backdrop-blur-xl">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.35em] mb-2">Discover</p>
              <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">Find the right event</h2>
              <p className="text-gray-500 text-sm mt-2">Search by title, venue, or browse by category.</p>
            </div>

            <div className="relative w-full lg:max-w-md">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search events..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full bg-black/30 border border-white/[0.07] rounded-2xl pl-11 pr-4 py-3 text-white outline-none focus:border-sky-500/40 text-sm placeholder:text-gray-600 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 hover:text-white text-sm font-bold transition-colors"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto pt-4 pb-1 scrollbar-hide">
            {CATEGORIES.filter(c => c.key === 'all' || events.some(e => e.category === c.key)).map(cat => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`shrink-0 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeCategory === cat.key
                    ? 'bg-sky-500 text-black'
                    : 'bg-black/30 text-gray-500 border border-white/[0.06] hover:text-white hover:border-sky-500/20'
                  }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </section>

        {/* SPOTLIGHT */}
        {spotlightEvent && (
          <section className="mb-10 grid grid-cols-1 lg:grid-cols-5 gap-5">
            <div className="lg:col-span-3 relative overflow-hidden rounded-[2rem] border border-white/[0.07] bg-white/[0.02] min-h-[320px]">
              {spotlightEvent.banner ? (
                <img
                  src={spotlightEvent.banner}
                  alt={spotlightEvent.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : null}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="relative z-10 flex h-full items-end p-6 sm:p-8">
                <div className="max-w-2xl">
                  <p className="text-[9px] font-black text-sky-400 uppercase tracking-[0.35em] mb-3">Spotlight Event</p>
                  <h3 className="text-2xl sm:text-4xl font-black text-white tracking-tight leading-[0.95] mb-3">
                    {spotlightEvent.name}
                  </h3>
                  <p className="text-sm text-gray-300 leading-relaxed max-w-xl line-clamp-3">
                    {spotlightEvent.description || 'Explore the full event details, competition segments, merch, and registration flow.'}
                  </p>
                  <div className="flex flex-wrap gap-3 mt-5">
                    {spotlightEvent.event_date && (
                      <span className="text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/10 text-white/70 border border-white/10">
                        {formatDate(spotlightEvent.event_date)}
                      </span>
                    )}
                    {spotlightEvent.venue && (
                      <span className="text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full bg-white/10 text-white/70 border border-white/10">
                        {spotlightEvent.venue}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => handleSelectEvent(spotlightEvent)}
                    className="mt-6 bg-white text-black hover:bg-sky-400 font-black px-6 py-3 rounded-2xl text-[10px] uppercase tracking-[0.25em] transition-all active:scale-95"
                  >
                    Open Event →
                  </button>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 grid gap-4">
              <div className="rounded-[2rem] border border-white/[0.06] bg-white/[0.025] p-6">
                <p className="text-[9px] font-black text-gray-600 uppercase tracking-[0.35em] mb-3">Why EventDeck</p>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-black text-white uppercase tracking-tight">Professional ticketing</p>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">Premium registration flow with digital QR entry credentials.</p>
                  </div>
                  <div>
                    <p className="text-sm font-black text-white uppercase tracking-tight">Built for team events</p>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">Support for multi-member submissions and team-aware exports.</p>
                  </div>
                  <div>
                    <p className="text-sm font-black text-white uppercase tracking-tight">Organizer-grade exports</p>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">Segment-aware CSV downloads and verification workflows.</p>
                  </div>
                </div>
              </div>

              <div className="rounded-[2rem] border border-white/[0.06] bg-white/[0.025] p-6">
                <p className="text-[9px] font-black text-gray-600 uppercase tracking-[0.35em] mb-3">For Participants</p>
                <p className="text-sm text-gray-400 leading-relaxed">
                  Register in minutes, track your status, and access a premium ticket experience from one place.
                </p>
                <button
                  onClick={() => navigate('/my-tickets')}
                  className="mt-5 border border-sky-500/20 bg-sky-500/10 hover:bg-sky-500 hover:text-black text-sky-400 font-black px-5 py-3 rounded-2xl text-[10px] uppercase tracking-[0.25em] transition-all active:scale-95"
                >
                  Open My Tickets
                </button>
              </div>
            </div>
          </section>
        )}

        {/* EVENTS GRID */}
        <section id="events-grid" className="mb-12">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
            <div>
              <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.35em] mb-2">Live Listings</p>
              <h2 className="text-2xl sm:text-4xl font-black text-white uppercase tracking-tighter">
                Explore Events
              </h2>
            </div>
            <p className="text-gray-500 text-sm">
              {filteredEvents.length} {filteredEvents.length === 1 ? 'event' : 'events'} available
            </p>
          </div>

          {filteredEvents.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {filteredEvents.map(event => (
                <div
                  key={event.id}
                  className="rounded-[2rem] border border-white/[0.06] bg-white/[0.02] p-2 hover:border-sky-500/20 transition-all duration-300 hover:-translate-y-1"
                >
                  <EventCard event={event} onClick={() => handleSelectEvent(event)} />
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-[2rem] border border-dashed border-white/[0.06] bg-white/[0.015] px-6 py-16 text-center">
              <p className="text-[10px] font-black text-gray-600 uppercase tracking-[0.35em] mb-3">
                No matching events
              </p>
              <p className="text-gray-500 text-sm">
                Try another category or clear your search to explore all live listings.
              </p>
            </div>
          )}
        </section>

        <AdSlot ad={homepageAd2} label="Official Partner" className="mb-12" />

        {/* CREDIBILITY / PAST EVENTS */}
        <section className="mb-16 rounded-[2.5rem] border border-white/[0.06] bg-white/[0.02] px-5 py-8 sm:px-8 sm:py-10">
          <div className="text-center mb-10">
            <p className="text-[9px] font-black text-sky-500 uppercase tracking-[0.35em] mb-3">Track Record</p>
            <h2 className="text-3xl sm:text-5xl font-black text-white uppercase tracking-tighter">
              Past Events
            </h2>
            <p className="text-gray-500 text-sm mt-3">
              Events successfully powered by EventDeck.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { name: 'BFSAE 2025', org: 'MIST BLITZ', participants: 320, date: 'June 2025', category: 'Competition' },
              { name: 'Mecha Fest 2025', org: 'MIST Robotics', participants: 180, date: 'March 2025', category: 'Festival' },
              { name: 'Tech Summit 2024', org: 'MIST CSE', participants: 450, date: 'December 2024', category: 'Conference' },
            ].map(ev => (
              <div
                key={ev.name}
                className="rounded-[2rem] border border-white/[0.06] bg-black/20 p-6 hover:border-sky-500/20 transition-all"
              >
                <span className="inline-flex text-[8px] font-black text-sky-500 uppercase tracking-widest bg-sky-500/10 border border-sky-500/20 px-2.5 py-1 rounded-full">
                  {ev.category}
                </span>
                <h3 className="text-lg font-black text-white uppercase tracking-tight mt-4 mb-1">
                  {ev.name}
                </h3>
                <p className="text-gray-500 text-xs mb-4">{ev.org} · {ev.date}</p>
                <div className="flex items-center gap-2 pt-4 border-t border-white/[0.05]">
                  <svg className="w-3 h-3 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-sm font-black text-white">{ev.participants}</span>
                  <span className="text-[9px] text-gray-500 uppercase tracking-widest">Participants</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    );
  };

  return (
    <GoogleOAuthProvider clientId="280165033145-7lhlaau9etj7q4tito8tc429k3l2lqsg.apps.googleusercontent.com">
      
        {/* This updates the browser head for social media previews */}
       
        {selectedEvent && (
          <Helmet>
            <title>{selectedEvent.name} | EventDeck</title>
            <meta property="og:title" content={selectedEvent.name} />
            <meta property="og:description" content={selectedEvent.description || "Register for this event on EventDeck."} />
            {/* Ensure your Django backend sends 'banner_url' as a full HTTPS link */}
            <meta property="og:image" content={selectedEvent.banner_url || selectedEvent.banner} />
            <meta property="og:url" content={window.location.href} />
            <meta property="og:type" content="website" />
            <meta name="twitter:card" content="summary_large_image" />
          </Helmet>
        )}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/portal" element={<ProtectedPortal><Portal /></ProtectedPortal>} />
        <Route path="/registration-success" element={<RegistrationSuccess />} />
        <Route path="/registration-fail" element={<RegistrationFail />} />
        <Route path="/event/:id/theater" element={<TheaterMode />} />
        <Route path="/participant/login" element={<ParticipantLogin />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/my-tickets" element={<ProtectedProfile><ParticipantProfile /></ProtectedProfile>} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/refunds" element={<RefundPolicy />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/help" element={<HelpCenter />} />
        <Route path="/docs" element={<DocsPage />} />
        <Route path="/staff" element={<StaffPage />} />

        {/* Added this catch-all pattern to the Event and Home routes 
      This ensures that when you are on /event/2, the background and 
      app logic (Nav, SuccessModal, etc) stay visible. */}
        <Route path="/event/:id" element={
          <div className="min-h-screen relative flex flex-col bg-[#050505]">
            <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
              <div className="absolute top-[-5%] left-[-10%] w-[80%] h-[40%] bg-blue-600/5 rounded-full blur-[120px]" />
              <div className="absolute bottom-[-5%] right-[-10%] w-[80%] h-[40%] bg-sky-500/5 rounded-full blur-[120px]" />
            </div>
            <SuccessModal isOpen={showSuccess} onClose={() => setShowSuccess(false)} />
            <Cart isOpen={cartOpen} onClose={() => setCartOpen(false)} onCheckoutSuccess={handleSuccess} />

            <nav className="fixed top-0 w-full z-50 bg-black/70 backdrop-blur-xl border-b border-white/[0.05]">
              <div className="max-w-7xl mx-auto px-4 md:px-6 py-3.5 sm:py-4 flex justify-between items-center">
                <div className="flex items-center gap-2 cursor-pointer transition-opacity active:opacity-70"
                  onClick={() => { setIsStaffMode(false); setSelectedEvent(null); setSelectedSegment(null); navigate('/'); }}>
                  <img
                    src="/logo192.png"
                    alt="EventDeck"
                    className="w-10 h-10 object-contain"
                  />
                  <h1 className="text-base sm:text-lg font-black tracking-tighter uppercase">EVENT<span className="text-sky-500">DECK</span></h1>
                </div>
                {selectedEvent && !isStaffMode && (
                  <div className="hidden sm:flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                    <button onClick={() => { setSelectedEvent(null); setSelectedSegment(null); navigate('/'); }} className="hover:text-white transition-colors">Events</button>
                    <span className="text-gray-700">›</span>
                    <button onClick={() => setSelectedSegment(null)} className={`hover:text-white transition-colors max-w-[140px] truncate ${!selectedSegment ? 'text-white' : ''}`}>{selectedEvent.name}</button>
                    {selectedSegment && (<><span className="text-gray-700">›</span><span className="text-sky-400 truncate max-w-[100px]">{selectedSegment.title}</span></>)}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {participantToken && (
                    <button onClick={() => setCartOpen(true)}
                      className="relative w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-gray-400 hover:text-white transition-colors rounded-xl hover:bg-white/5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </button>
                  )}
                  {participantToken && participantUser ? (
                    <div className="flex items-center gap-2">
                      <button onClick={() => window.location.href = '/my-tickets'}
                        className="text-[9px] font-black text-gray-400 hover:text-white uppercase tracking-widest transition-colors px-2 py-1.5 rounded-lg hover:bg-white/5">
                        Tickets
                      </button>
                      <button onClick={handleParticipantLogout}
                        className="hidden sm:block text-[9px] font-black text-gray-600 hover:text-red-400 uppercase tracking-widest transition-colors">
                        Sign Out
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 scale-90 sm:scale-100 origin-right">
                      <button onClick={() => window.location.href = '/participant/login'}
                        className="text-[9px] font-black text-white/40 hover:text-white uppercase tracking-widest transition-colors px-2">
                        Sign In
                      </button>
                      <GoogleLogin
                        onSuccess={handleGoogleLoginSuccess}
                        onError={() => console.log('Login Failed')}
                        useOneTap
                        theme="filled_blue"
                        shape="pill"
                        size="small"
                        text="signin_with"
                      />
                    </div>
                  )}
                </div>
              </div>
            </nav>

            <main className="flex-grow relative z-10">
              {isStaffMode ? (
                <section className="max-w-4xl mx-auto px-4 md:px-6 pt-24 sm:pt-28 animate-in fade-in zoom-in duration-500">
                  <div className="flex gap-3 sm:gap-4 mb-8 sm:mb-10 justify-center">
                    <button onClick={() => setStaffView('scanner')} className={`px-5 sm:px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${staffView === 'scanner' ? 'bg-sky-500 text-black' : 'bg-white/5 text-gray-500 border border-white/5'}`}>Gate Scanner</button>
                    <button onClick={() => setStaffView('dashboard')} className={`px-5 sm:px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${staffView === 'dashboard' ? 'bg-sky-500 text-black' : 'bg-white/5 text-gray-500 border border-white/5'}`}>Intelligence</button>
                  </div>
                  {staffView === 'scanner' ? <Scanner /> : <Dashboard eventId={events[0]?.id || 1} />}
                </section>
              ) : (
                <PageView viewKey={viewKey}>
                  {selectedSegment ? renderRegistration() : selectedEvent ? renderEventDetail() : renderHomepage()}
                </PageView>
              )}
            </main>

            {!isStaffMode && !selectedSegment && (
              <footer className="relative z-10 bg-black border-t border-white/[0.05] pt-10 pb-8 px-4 md:px-6 mt-8">
                <div className="max-w-7xl mx-auto">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-10">
                    <div className="sm:col-span-1">
                      <div className="flex items-center gap-2 mb-3">
                        <img
                          src="/logo192.png"
                          alt="EventDeck"
                          className="w-10 h-10 object-contain"
                        />
                        <h1 className="text-sm font-black tracking-tight uppercase">EVENT<span className="text-sky-500">DECK</span></h1>
                      </div>
                      <p className="text-gray-600 text-xs leading-relaxed max-w-[220px]">Global infrastructure for elite engineering events.</p>
                      <div className="flex items-center gap-2 mt-4">
                        <a href="PASTE_FACEBOOK_URL_HERE" target="_blank" rel="noopener noreferrer"
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] border border-white/[0.07] hover:border-blue-500/40 hover:bg-blue-500/10 transition-all group">
                          <svg className="w-3.5 h-3.5 text-gray-600 group-hover:text-blue-400 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                          </svg>
                        </a>
                        <a href="PASTE_INSTAGRAM_URL_HERE" target="_blank" rel="noopener noreferrer"
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] border border-white/[0.07] hover:border-pink-500/40 hover:bg-pink-500/10 transition-all group">
                          <svg className="w-3.5 h-3.5 text-gray-600 group-hover:text-pink-400 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                          </svg>
                        </a>
                        <a href="mailto:hello@eventdeck.app"
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] border border-white/[0.07] hover:border-sky-500/40 hover:bg-sky-500/10 transition-all group">
                          <svg className="w-3.5 h-3.5 text-gray-600 group-hover:text-sky-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </a>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-white text-[10px] font-bold uppercase tracking-widest mb-4">Legal</h4>
                      <ul className="space-y-3">
                        <li onClick={() => window.location.href = '/privacy'} className="text-gray-600 text-xs hover:text-sky-500 cursor-pointer transition-colors">Privacy Policy</li>
                        <li onClick={() => window.location.href = '/terms'} className="text-gray-600 text-xs hover:text-sky-500 cursor-pointer transition-colors">Terms of Service</li>
                        <li onClick={() => window.location.href = '/refunds'} className="text-gray-600 text-xs hover:text-sky-500 cursor-pointer transition-colors">Refund Policy</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-white text-[10px] font-bold uppercase tracking-widest mb-4">Support</h4>
                      <ul className="space-y-3">
                        <li onClick={() => window.location.href = '/contact'} className="text-gray-600 text-xs hover:text-sky-500 cursor-pointer transition-colors">Contact Us</li>
                        <li onClick={() => window.location.href = '/help'} className="text-gray-600 text-xs hover:text-sky-500 cursor-pointer transition-colors">Help Center</li>
                        <li onClick={() => window.location.href = '/docs'} className="text-gray-600 text-xs hover:text-sky-500 cursor-pointer transition-colors">Documentation</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mb-8">
                    <a href="https://wa.me/message/VKMXTZKK5LITK1" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-4 bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/15 hover:border-emerald-500/40 px-5 py-4 rounded-2xl transition-all w-full sm:w-auto sm:inline-flex"
                    >
                      <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center shrink-0">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm.029 18.88a7.945 7.945 0 01-3.8-.966L4.5 19.5l1.612-3.634A7.975 7.975 0 0112.029 4a7.975 7.975 0 010 15.88z" />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-black text-emerald-400 uppercase tracking-widest leading-none mb-1">Host Your Event with EventDeck</p>
                        <p className="text-xs text-gray-500 leading-relaxed">Want professional ticketing for your event? Contact us on WhatsApp →</p>
                      </div>
                    </a>
                  </div>
                  <div className="border-t border-white/[0.05] pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <p className="text-[9px] text-gray-700 uppercase tracking-[0.4em] font-medium text-center sm:text-left">
                      © 2026 Event Deck Global Infrastructure
                    </p>
                  </div>
                </div>
              </footer>
            )}
          </div>
        } />

        <Route path="/" element={
          <div className="min-h-screen relative flex flex-col bg-[#050505]">
            <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
              <div className="absolute top-[-5%] left-[-10%] w-[80%] h-[40%] bg-blue-600/5 rounded-full blur-[120px]" />
              <div className="absolute bottom-[-5%] right-[-10%] w-[80%] h-[40%] bg-sky-500/5 rounded-full blur-[120px]" />
            </div>
            <SuccessModal isOpen={showSuccess} onClose={() => setShowSuccess(false)} />
            <Cart isOpen={cartOpen} onClose={() => setCartOpen(false)} onCheckoutSuccess={handleSuccess} />

            <nav className="fixed top-0 w-full z-50 bg-black/70 backdrop-blur-xl border-b border-white/[0.05]">
              <div className="max-w-7xl mx-auto px-4 md:px-6 py-3.5 sm:py-4 flex justify-between items-center">
                <div className="flex items-center gap-2 cursor-pointer transition-opacity active:opacity-70"
                  onClick={() => { setIsStaffMode(false); setSelectedEvent(null); setSelectedSegment(null); }}>
                  <img
                    src="/logo192.png"
                    alt="EventDeck"
                    className="w-10 h-10 object-contain"
                  />
                  <h1 className="text-base sm:text-lg font-black tracking-tighter uppercase">EVENT<span className="text-sky-500">DECK</span></h1>
                </div>
                {selectedEvent && !isStaffMode && (
                  <div className="hidden sm:flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                    <button onClick={() => { setSelectedEvent(null); setSelectedSegment(null); }} className="hover:text-white transition-colors">Events</button>
                    <span className="text-gray-700">›</span>
                    <button onClick={() => setSelectedSegment(null)} className={`hover:text-white transition-colors max-w-[140px] truncate ${!selectedSegment ? 'text-white' : ''}`}>{selectedEvent.name}</button>
                    {selectedSegment && (<><span className="text-gray-700">›</span><span className="text-sky-400 truncate max-w-[100px]">{selectedSegment.title}</span></>)}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  {participantToken && (
                    <button onClick={() => setCartOpen(true)}
                      className="relative w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-gray-400 hover:text-white transition-colors rounded-xl hover:bg-white/5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </button>
                  )}
                  {participantToken && participantUser ? (
                    <div className="flex items-center gap-2">
                      <button onClick={() => window.location.href = '/my-tickets'}
                        className="text-[9px] font-black text-gray-400 hover:text-white uppercase tracking-widest transition-colors px-2 py-1.5 rounded-lg hover:bg-white/5">
                        Tickets
                      </button>
                      <button onClick={handleParticipantLogout}
                        className="hidden sm:block text-[9px] font-black text-gray-600 hover:text-red-400 uppercase tracking-widest transition-colors">
                        Sign Out
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 scale-90 sm:scale-100 origin-right">
                      <button onClick={() => window.location.href = '/participant/login'}
                        className="text-[9px] font-black text-white/40 hover:text-white uppercase tracking-widest transition-colors px-2">
                        Sign In
                      </button>
                      <GoogleLogin
                        onSuccess={handleGoogleLoginSuccess}
                        onError={() => console.log('Login Failed')}
                        useOneTap
                        theme="filled_blue"
                        shape="pill"
                        size="small"
                        text="signin_with"
                      />
                    </div>
                  )}
                </div>
              </div>
            </nav>

            <main className="flex-grow relative z-10">
              {isStaffMode ? (
                <section className="max-w-4xl mx-auto px-4 md:px-6 pt-24 sm:pt-28 animate-in fade-in zoom-in duration-500">
                  <div className="flex gap-3 sm:gap-4 mb-8 sm:mb-10 justify-center">
                    <button onClick={() => setStaffView('scanner')} className={`px-5 sm:px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${staffView === 'scanner' ? 'bg-sky-500 text-black' : 'bg-white/5 text-gray-500 border border-white/5'}`}>Gate Scanner</button>
                    <button onClick={() => setStaffView('dashboard')} className={`px-5 sm:px-8 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${staffView === 'dashboard' ? 'bg-sky-500 text-black' : 'bg-white/5 text-gray-500 border border-white/5'}`}>Intelligence</button>
                  </div>
                  {staffView === 'scanner' ? <Scanner /> : <Dashboard eventId={events[0]?.id || 1} />}
                </section>
              ) : (
                <PageView viewKey={viewKey}>
                  {selectedSegment ? renderRegistration() : selectedEvent ? renderEventDetail() : renderHomepage()}
                </PageView>
              )}
            </main>

            {!isStaffMode && !selectedSegment && (
              <footer className="relative z-10 bg-black border-t border-white/[0.05] pt-10 pb-8 px-4 md:px-6 mt-8">
                <div className="max-w-7xl mx-auto">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-10">
                    <div className="sm:col-span-1">
                      <div className="flex items-center gap-2 mb-3">
                        <img
                          src="/logo192.png"
                          alt="EventDeck"
                          className="w-10 h-10 object-contain"
                        />
                        <h1 className="text-sm font-black tracking-tight uppercase">EVENT<span className="text-sky-500">DECK</span></h1>
                      </div>
                      <p className="text-gray-600 text-xs leading-relaxed max-w-[220px]">Global infrastructure for elite engineering events.</p>
                      <div className="flex items-center gap-2 mt-4">
                        <a href="PASTE_FACEBOOK_URL_HERE" target="_blank" rel="noopener noreferrer"
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] border border-white/[0.07] hover:border-blue-500/40 hover:bg-blue-500/10 transition-all group">
                          <svg className="w-3.5 h-3.5 text-gray-600 group-hover:text-blue-400 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                          </svg>
                        </a>
                        <a href="PASTE_INSTAGRAM_URL_HERE" target="_blank" rel="noopener noreferrer"
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] border border-white/[0.07] hover:border-pink-500/40 hover:bg-pink-500/10 transition-all group">
                          <svg className="w-3.5 h-3.5 text-gray-600 group-hover:text-pink-400 transition-colors" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                          </svg>
                        </a>
                        <a href="mailto:hello@eventdeck.app"
                          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.04] border border-white/[0.07] hover:border-sky-500/40 hover:bg-sky-500/10 transition-all group">
                          <svg className="w-3.5 h-3.5 text-gray-600 group-hover:text-sky-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </a>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-white text-[10px] font-bold uppercase tracking-widest mb-4">Legal</h4>
                      <ul className="space-y-3">
                        <li onClick={() => window.location.href = '/privacy'} className="text-gray-600 text-xs hover:text-sky-500 cursor-pointer transition-colors">Privacy Policy</li>
                        <li onClick={() => window.location.href = '/terms'} className="text-gray-600 text-xs hover:text-sky-500 cursor-pointer transition-colors">Terms of Service</li>
                        <li onClick={() => window.location.href = '/refunds'} className="text-gray-600 text-xs hover:text-sky-500 cursor-pointer transition-colors">Refund Policy</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="text-white text-[10px] font-bold uppercase tracking-widest mb-4">Support</h4>
                      <ul className="space-y-3">
                        <li onClick={() => window.location.href = '/contact'} className="text-gray-600 text-xs hover:text-sky-500 cursor-pointer transition-colors">Contact Us</li>
                        <li onClick={() => window.location.href = '/help'} className="text-gray-600 text-xs hover:text-sky-500 cursor-pointer transition-colors">Help Center</li>
                        <li onClick={() => window.location.href = '/docs'} className="text-gray-600 text-xs hover:text-sky-500 cursor-pointer transition-colors">Documentation</li>
                      </ul>
                    </div>
                  </div>
                  <div className="mb-8">
                    <a href="https://wa.me/message/VKMXTZKK5LITK1" target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-4 bg-emerald-500/10 border border-emerald-500/20 hover:bg-emerald-500/15 hover:border-emerald-500/40 px-5 py-4 rounded-2xl transition-all w-full sm:w-auto sm:inline-flex"
                    >
                      <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center shrink-0">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm.029 18.88a7.945 7.945 0 01-3.8-.966L4.5 19.5l1.612-3.634A7.975 7.975 0 0112.029 4a7.975 7.975 0 010 15.88z" />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-black text-emerald-400 uppercase tracking-widest leading-none mb-1">Host Your Event with EventDeck</p>
                        <p className="text-xs text-gray-500 leading-relaxed">Want professional ticketing for your event? Contact us on WhatsApp →</p>
                      </div>
                    </a>
                  </div>
                  <div className="border-t border-white/[0.05] pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                    <p className="text-[9px] text-gray-700 uppercase tracking-[0.4em] font-medium text-center sm:text-left">
                      © 2026 Event Deck Global Infrastructure
                    </p>
                  </div>
                </div>
              </footer>
            )}
          </div>
        } />
      </Routes>
      
    </GoogleOAuthProvider>
  );
}
// This helper sits INSIDE the Router to handle URL changes

export default App;